
import React from 'react';
import { createPortal } from 'react-dom';
import { SITE_CONTENT } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const ITEMS_PER_PAGE = 12;

const GalleryItem: React.FC<{
  url: string;
  caption: string;
  index: number;
  onClick: () => void;
}> = ({ url, caption, index, onClick }) => {
  const [src, setSrc] = React.useState(url);
  const [retryCount, setRetryCount] = React.useState(0);
  const extensions = ['.jpg', '.jpeg', '.JPG'];
  
  const handleError = () => {
    if (retryCount < extensions.length - 1) {
      const nextExt = extensions[retryCount + 1];
      // Separiamo il percorso dal parametro di versione (cache busting)
      const [path, query] = url.split('?');
      const basePath = path.substring(0, path.lastIndexOf('.'));
      setSrc(`${basePath}${nextExt}${query ? '?' + query : ''}`);
      setRetryCount(prev => prev + 1);
    } else {
      setSrc("https://images.unsplash.com/photo-1514320298574-255c5df6b0a1?q=80&w=2070&auto=format&fit=crop");
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % ITEMS_PER_PAGE) * 0.05 }}
      className="group relative p-1 bg-midnight border border-gold/10 cursor-pointer aspect-square"
      onClick={onClick}
    >
      {/* Decorative Corners - Inset slightly */}
      <div className="absolute top-1 left-1 w-3 h-3 border-t border-l border-gold/60 group-hover:w-5 group-hover:h-5 transition-all duration-500 z-20" />
      <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-gold/60 group-hover:w-5 group-hover:h-5 transition-all duration-500 z-20" />
      
      <div className="relative w-full h-full overflow-hidden bg-slate-800">
        <img 
          src={src} 
          alt={caption} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
          loading="lazy"
          onError={handleError}
        />
        <div className="absolute inset-0 bg-midnight/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center p-6 text-center">
          <Maximize2 className="text-gold w-8 h-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500" />
        </div>
      </div>
    </motion.div>
  );
};

const LightboxImage: React.FC<{ url: string; caption: string }> = ({ url, caption }) => {
  const [src, setSrc] = React.useState(url);
  const [retryCount, setRetryCount] = React.useState(0);
  const extensions = ['.jpg', '.jpeg', '.JPG'];

  React.useEffect(() => {
    setSrc(url);
    setRetryCount(0);
  }, [url]);

  const handleError = () => {
    if (retryCount < extensions.length - 1) {
      const nextExt = extensions[retryCount + 1];
      // Separiamo il percorso dal parametro di versione (cache busting)
      const [path, query] = url.split('?');
      const basePath = path.substring(0, path.lastIndexOf('.'));
      setSrc(`${basePath}${nextExt}${query ? '?' + query : ''}`);
      setRetryCount(prev => prev + 1);
    }
  };

  return (
    <div className="relative p-1 bg-midnight border border-gold/20 shadow-2xl">
      {/* Decorative Corners for Lightbox - Smaller and on top of the image padding */}
      <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-gold/50 z-10" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-gold/50 z-10" />
      
      <img 
        src={src} 
        alt={caption}
        className="max-w-full h-auto max-h-[80vh] object-contain shadow-2xl transition-transform duration-500"
        onError={handleError}
      />
    </div>
  );
};

const Gallery: React.FC = () => {
  const [visibleCount, setVisibleCount] = React.useState(ITEMS_PER_PAGE);
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);
  
  const allImages = SITE_CONTENT.gallery;
  const visibleImages = allImages.slice(0, visibleCount);
  const hasMore = visibleCount < allImages.length;

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, allImages.length));
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const nextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedImageIndex !== null) {
      const nextIndex = (selectedImageIndex + 1) % allImages.length;
      
      // Se stiamo visualizzando una delle ultime immagini caricate, carichiamo il set successivo
      if (nextIndex >= visibleCount - 2 && hasMore) {
        loadMore();
      }
      
      setSelectedImageIndex(nextIndex);
    }
  };

  const prevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + allImages.length) % allImages.length);
    }
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImageIndex]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-center mb-20">
        <span className="text-gold font-sans uppercase tracking-[0.4em] text-xs block mb-4">I nostri ricordi</span>
        <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">Gallery</h2>
        <div className="w-24 h-px bg-gold mx-auto" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {visibleImages.map((item, idx) => (
          <GalleryItem 
            key={idx} 
            url={item.url} 
            caption={item.caption} 
            index={idx}
            onClick={() => openLightbox(idx)}
          />
        ))}
      </div>

      {hasMore && (
        <div className="mt-16 text-center">
          <button 
            onClick={loadMore}
            className="px-10 py-4 border border-gold/40 text-gold hover:bg-gold hover:text-midnight transition-all duration-500 uppercase tracking-[0.3em] text-xs"
          >
            Carica Altre Foto
          </button>
          <p className="mt-4 text-white/30 text-xs uppercase tracking-widest">
            Visualizzate {visibleCount} di {allImages.length} immagini
          </p>
        </div>
      )}

      {/* Lightbox Modal - Rendered via Portal to body to avoid stacking context issues */}
      {isMounted && typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedImageIndex !== null && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] bg-midnight overflow-y-auto"
              onClick={closeLightbox}
            >
              <div 
                className="min-h-screen w-full flex flex-col items-center justify-start py-24 md:py-32 px-4 md:px-10 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={(_, info) => {
                    if (info.offset.x > 100) prevImage();
                    else if (info.offset.x < -100) nextImage();
                  }}
                  className="relative max-w-5xl w-full flex flex-col items-center py-12"
                >
                  {/* Desktop UI - Positioned relative to the image container */}
                  <div className="hidden lg:block">
                    {/* Back Button above Left Arrow */}
                    <button 
                      className="absolute left-[-100px] top-[-60px] text-gold hover:text-white transition-all z-[130] flex items-center space-x-2 group bg-midnight/40 px-3 py-1 rounded-md"
                      onClick={closeLightbox}
                    >
                      <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                      <span className="text-xs font-bold uppercase tracking-widest border-b border-gold/30 pb-1">Indietro</span>
                    </button>

                    {/* Close Button above Right Arrow area */}
                    <button 
                      className="absolute right-[-100px] top-[-60px] text-white/60 hover:text-gold transition-colors z-[130] bg-midnight/40 p-1 rounded-md"
                      onClick={closeLightbox}
                    >
                      <X size={32} />
                    </button>

                    {/* Navigation Arrows */}
                    <button 
                      className="absolute left-[-80px] top-[40%] -translate-y-1/2 text-white/40 hover:text-gold transition-colors z-[110] bg-white/5 hover:bg-white/10 p-4 rounded-full backdrop-blur-sm"
                      onClick={prevImage}
                      title="Precedente"
                    >
                      <ChevronLeft size={48} />
                    </button>

                    <button 
                      className="absolute right-[-80px] top-[40%] -translate-y-1/2 text-white/40 hover:text-gold transition-colors z-[110] bg-white/5 hover:bg-white/10 p-4 rounded-full backdrop-blur-sm"
                      onClick={nextImage}
                      title="Successiva"
                    >
                      <ChevronRight size={48} />
                    </button>
                  </div>

                  {/* Mobile/Tablet UI Header */}
                  <div className="lg:hidden fixed top-0 left-0 w-full p-4 flex justify-between items-center z-[130] bg-midnight border-b border-gold/20">
                    <button 
                      className="text-gold flex items-center space-x-2 px-3 py-1"
                      onClick={closeLightbox}
                    >
                      <ChevronLeft size={20} />
                      <span className="text-xs font-bold uppercase tracking-widest">Gallery</span>
                    </button>
                    <button 
                      className="text-white/80 p-1"
                      onClick={closeLightbox}
                    >
                      <X size={28} />
                    </button>
                  </div>

                  {/* Smaller screens navigation arrows (overlay on image) */}
                  <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none lg:hidden">
                     <button 
                      className="pointer-events-auto text-white/60 hover:text-gold transition-colors z-[110] bg-midnight/40 p-2 rounded-full backdrop-blur-sm"
                      onClick={prevImage}
                    >
                      <ChevronLeft size={32} />
                    </button>
                    <button 
                      className="pointer-events-auto text-white/60 hover:text-gold transition-colors z-[110] bg-midnight/40 p-2 rounded-full backdrop-blur-sm"
                      onClick={nextImage}
                    >
                      <ChevronRight size={32} />
                    </button>
                  </div>

                  <div className="relative group cursor-pointer" onClick={nextImage}>
                    <LightboxImage 
                      url={allImages[selectedImageIndex].url} 
                      caption={allImages[selectedImageIndex].caption} 
                    />
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <span className="bg-midnight/80 text-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-widest backdrop-blur-md border border-gold/20">
                        Clicca per la prossima
                      </span>
                    </div>
                  </div>

                  <div className="mt-8 text-center px-4">
                    <p className="text-white/40 text-xs uppercase tracking-[0.4em]">
                      {selectedImageIndex + 1} / {allImages.length}
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Gallery;
